import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  ClipboardList,
  FolderKanban,
  LayoutDashboard,
  type LucideIcon,
  MessageCircle,
  User as UserIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge.js';
import { useGetUnreadTotalQuery } from '@/features/chat/chatApi.js';
import { FEATURES } from '@/shared/config/features.js';
import { cn } from '@/lib/utils.js';

interface BottomNavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  badgeKey?: 'chat';
}

const ITEMS: BottomNavItem[] = [
  { label: 'Дом', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Проекты', icon: FolderKanban, path: '/projects' },
  ...(FEATURES.TASKS ? [{ label: 'Задачи', icon: ClipboardList, path: '/tasks' }] : []),
  ...(FEATURES.MESSENGER
    ? [{ label: 'Чат', icon: MessageCircle, path: '/chat', badgeKey: 'chat' as const }]
    : []),
  { label: 'Я', icon: UserIcon, path: '/profile' },
];

// В открытом чате разделы не нужны: из чата выходят кнопкой «назад», а в PWA на iOS
// панель ещё и всплывала между полем ввода и клавиатурой.
export function hidesBottomNav(pathname: string): boolean {
  return /^\/chat\/[^/]+/.test(pathname);
}

/**
 * Bottom navigation — mobile-only (sm:hidden).
 * 5 most-used routes. Active item is enlarged + gets gradient indicator.
 */
export function BottomNav() {
  const location = useLocation();
  const { data: unreadData } = useGetUnreadTotalQuery(undefined, {
    skip: !FEATURES.MESSENGER,
    pollingInterval: 30000,
  });
  const chatUnread = unreadData?.data?.count ?? 0;

  if (hidesBottomNav(location.pathname)) return null;

  return (
    <nav
      aria-label="Bottom navigation"
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 sm:hidden',
        'border-t border-border-subtle bg-background/85 backdrop-blur-xl',
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="flex h-16 items-stretch justify-around px-1">
        {ITEMS.map((item) => {
          const Icon = item.icon;
          const active =
            location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const badge = item.badgeKey === 'chat' && chatUnread > 0 ? chatUnread : 0;

          return (
            <li key={item.path} className="flex-1">
              <NavLink
                to={item.path}
                className={cn(
                  'group relative flex h-full flex-col items-center justify-center gap-0.5',
                  'transition-colors',
                  active ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {active && (
                  <motion.span
                    layoutId="bottom-nav-indicator"
                    className="absolute top-0 h-1 w-10 rounded-b-full bg-gradient-primary"
                    transition={{ type: 'spring', damping: 22, stiffness: 320 }}
                    aria-hidden
                  />
                )}
                <span className="relative grid h-7 w-7 place-items-center">
                  <motion.span
                    animate={{ scale: active ? 1.1 : 1 }}
                    transition={{ duration: 0.2 }}
                    className="grid place-items-center"
                  >
                    <Icon
                      className={cn(
                        'h-5 w-5',
                        active && 'drop-shadow-[0_0_8px_rgba(99,102,241,0.4)]',
                      )}
                    />
                  </motion.span>
                  {badge > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-2 h-4 min-w-[16px] rounded-full border-background px-1 text-[10px] leading-none"
                    >
                      {badge > 99 ? '99+' : badge}
                    </Badge>
                  )}
                </span>
                <span
                  className={cn(
                    'text-[10px] font-medium leading-none transition-opacity',
                    active ? 'opacity-100' : 'opacity-70',
                  )}
                >
                  {item.label}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
